import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Chip,
  Tooltip,
  useTheme as useMuiTheme,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  Email as EmailIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { getImageUrl } from '../utils/formatters';
import api from '../services/api';
import MeuPerfil from './usuarios/MeuPerfil';

const Header = ({ 
  sidebarOpen, 
  handleSidebarToggle,
  atendenteSelecionado, 
  customTheme,
  title
}) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { user, signOut } = useAuth();
  const muiTheme = useMuiTheme(); // Fallback theme
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Estados para controle de erro de imagem
  const [userPhotoError, setUserPhotoError] = useState(false);
  const [atendentePhotoError, setAtendentePhotoError] = useState(false);
  const [userData, setUserData] = useState(null);
  const [openPerfilModal, setOpenPerfilModal] = useState(false);
  
  // Carregar dados completos do usuário, incluindo imagem de perfil atualizada
  useEffect(() => {
    const carregarDadosUsuario = async () => {
      if (user && user.id) {
        // console.log('[Header] Carregando dados completos do usuário ID:', user.id);
        try {
          const response = await api.get(`/usuarios/${user.id}`);
          if (response.data && response.data.success) {
            setUserData(response.data.data);
            // console.log('[Header] Dados do usuário carregados:', response.data.data);
            // console.log('[Header] Foto de perfil:', response.data.data.imagem_perfil);
          }
        } catch (error) {
          console.error('[Header] Erro ao carregar dados do usuário:', error);
        }
      }
    };
    
    carregarDadosUsuario();
  }, [user]);
  
  // Use customTheme if provided, otherwise fall back to muiTheme
  const theme = customTheme || muiTheme;

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    signOut();
  };

  const handleOpenPerfilModal = () => {
    handleUserMenuClose();
    setOpenPerfilModal(true);
  };

  // Função para mostrar iniciais do nome caso não haja foto
  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  // Função para manipular erros de carregamento de imagens
  const handleUserImageError = () => {
    console.log('[Header] Erro ao carregar foto do usuário');
    setUserPhotoError(true);
  };
  
  const handleAtendenteImageError = () => {
    console.log('[Header] Erro ao carregar foto do atendente');
    setAtendentePhotoError(true);
  };

  // Obter a URL da imagem do usuário usando a utilidade getImageUrl
  const userPhotoPath = userData?.imagem_perfil || user?.imagem_perfil || null;
  const userPhotoUrl = !userPhotoError && userPhotoPath ? getImageUrl(userPhotoPath) : '';
  
  // Log para debug
  // console.log('[Header] Caminho da foto do usuário:', userPhotoPath);
  // console.log('[Header] URL da foto do usuário:', userPhotoUrl);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: sidebarOpen ? `calc(100% - 240px)` : `calc(100% - 80px)`,
        ml: sidebarOpen ? '280px' : '80px',
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: `1px solid ${theme.palette.divider}`,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        zIndex: (theme) => theme.zIndex.drawer - 1,
      }}
    >
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="toggle sidebar"
          onClick={handleSidebarToggle}
          sx={{ ml: sidebarOpen ? 4 : 0 }}
        >
          {sidebarOpen ? <ArrowBackIcon /> : <MenuIcon />}
        </IconButton>

        {title && (
          <Typography variant="h6" fontWeight="600" noWrap sx={{ ml: 2 }}>
            {title}
          </Typography>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title={isDarkMode ? "Mudar para modo claro" : "Mudar para modo escuro"}>
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              sx={{
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'rotate(30deg)',
                },
              }}
            >
              {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {atendenteSelecionado && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                avatar={
                  <Avatar
                    alt={atendenteSelecionado.nome}
                    src={!atendentePhotoError && atendenteSelecionado.foto ? getImageUrl(atendenteSelecionado.foto) : ''}
                    onError={handleAtendenteImageError}
                    sx={{ bgcolor: theme.palette.secondary.main }}
                  >
                    {getInitials(atendenteSelecionado.nome)}
                  </Avatar>
                }
                label={atendenteSelecionado.nome}
                variant="outlined"
                sx={{
                  borderColor: 'transparent',
                  '& .MuiChip-label': {
                    color: 'text.primary',
                  },
                }}
              />
            </Box>
          )}

          {user && (
            <>
              <Tooltip title="Perfil de usuário">
                <Chip
                  avatar={
                    <Avatar 
                      alt={user.nome} 
                      src={userPhotoUrl}
                      onError={handleUserImageError}
                      sx={{ bgcolor: theme.palette.primary.main }}
                    >
                      {getInitials(user.nome)}
                    </Avatar>
                  }
                  label={user.nome}
                  onClick={handleUserMenuOpen}
                  sx={{
                    cursor: 'pointer',
                    borderColor: theme.palette.divider,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: theme.palette.action.hover,
                    },
                    '& .MuiChip-label': {
                      color: 'text.primary',
                    },
                  }}
                />
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  elevation: 2,
                  sx: {
                    overflow: 'visible',
                    mt: 1.5,
                    width: 220,
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                }}
                TransitionProps={{
                  appear: true,
                  timeout: 200,
                }}
                slotProps={{
                  paper: {
                    sx: {
                      opacity: 1,
                      transform: 'none',
                      transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 133ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                    }
                  }
                }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 1.5,
                    gap: 2
                  }}>
                    <Avatar
                      alt={user.nome}
                      src={userPhotoUrl}
                      sx={{ 
                        width: 56, 
                        height: 56,
                        bgcolor: theme.palette.primary.main,
                        boxShadow: '0 3px 5px rgba(0,0,0,0.1)'
                      }}
                    >
                      {getInitials(user.nome)}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight="500">{user.nome}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
                        <EmailIcon fontSize="small" color="action" sx={{ fontSize: 14 }} />
                        <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
                        <WorkIcon fontSize="small" color="primary" sx={{ fontSize: 14 }} />
                        <Typography variant="caption" color="primary">{user.cargo}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Divider />
                <MenuItem onClick={handleOpenPerfilModal} sx={{ 
                  gap: 1, 
                  borderRadius: '4px',
                  mx: 1,
                  my: 0.5, 
                  '&:hover': { 
                    bgcolor: theme => theme.palette.action.hover 
                  } 
                }}>
                  <AccountCircleIcon fontSize="small" />
                  <Typography variant="body2">Meu Perfil</Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout} sx={{ 
                  gap: 1, 
                  borderRadius: '4px',
                  mx: 1,
                  my: 0.5,
                  color: 'error.main',
                  '&:hover': { 
                    bgcolor: theme => theme.palette.mode === 'dark' 
                      ? 'rgba(244, 67, 54, 0.08)'
                      : 'rgba(244, 67, 54, 0.04)'
                  } 
                }}>
                  <ExitToAppIcon fontSize="small" />
                  <Typography variant="body2">Sair</Typography>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
      
      {/* Modal de Meu Perfil */}
      <MeuPerfil 
        open={openPerfilModal} 
        onClose={() => setOpenPerfilModal(false)}
      />
    </AppBar>
  );
};

export default Header; 